/**
 * Math delimiter normalization for markdown rendering.
 *
 * LLMs emit math with a few different delimiters (`\( \)`, `\[ \]`, `$$ $$`),
 * while `remark-math` only understands `$`/`$$`. This module rewrites the
 * common LaTeX delimiters into the `$`/`$$` forms remark-math parses.
 *
 * IMPORTANT — currency safety: our product is full of dollar amounts
 * ("you pay $5 for $10 of coverage"). The default `remark-math` behaviour
 * treats single `$...$` as inline math, which would pair adjacent dollar
 * signs and mangle real numbers into garbled formulas. To prevent that, we
 * treat every *literal* single `$` as currency and escape it (`\$`), so the
 * ONLY `$`/`$$` that reach remark-math are the ones we synthesize from
 * explicit `\( \)` / `\[ \]` / `$$ $$` delimiters.
 *
 * Net effect:
 *   - `\(x\)`      -> inline math
 *   - `\[x\]`      -> display math
 *   - `$$x$$`      -> display math
 *   - `$5`, `$10`  -> literal currency (never math)
 *   - a bare `$x$` written by the model -> literal text (won't render as math)
 *
 * Code spans and fenced code blocks are left completely untouched.
 */

// Sentinel uses a Private Use Area code point (U+E000), which never appears in
// markdown source or LLM output, so the escape pass below can't accidentally
// mangle it. PUA rather than NUL keeps it out of the "control character" lint
// while preserving the "never collides with real text" property.
const SENTINEL = "\uE000";
const STASH = new RegExp(String.raw`${SENTINEL}M(\d+)${SENTINEL}`, "g");

type StashKind = "inline" | "display";

function transformProse(text: string): string {
  const store: Array<[StashKind, string]> = [];
  const stash = (kind: StashKind, tex: string): string => {
    const i = store.length;
    // KaTeX treats a bare `,` as punctuation and adds a trailing space, so a
    // thousands separator like `$5,330` renders as `$5, 330`. Rewrite commas
    // that sit between two digits to `{,}` (an ordinary atom, no extra space)
    // so currency renders tight. Only tight `\d,\d` is touched — enumerations
    // like `1, 2, 3` keep their space, so they're left alone.
    store.push([kind, tex.replace(/(\d),(\d)/g, "$1{,}$2")]);
    return `${SENTINEL}M${i}${SENTINEL}`;
  };

  let s = text;

  // Pull out real math first so its inner `$` can't be escaped as currency.
  // Order matters: handle `$$…$$` before the single-char escapes.
  s = s.replace(/\$\$([\s\S]+?)\$\$/g, (_m, tex: string) => stash("display", tex));
  s = s.replace(/\\\[([\s\S]+?)\\\]/g, (_m, tex: string) => stash("display", tex));
  s = s.replace(/\\\(([\s\S]+?)\\\)/g, (_m, tex: string) => stash("inline", tex));

  // Everything left is literal: escape unescaped `$` so remark-math ignores it.
  s = s.replace(/(?<!\\)\$/g, String.raw`\$`);

  // Restore real math. Display math is wrapped in its own block so KaTeX
  // renders it centered on its own line; inline stays in the text flow.
  s = s.replace(STASH, (_m, idx: string) => {
    const entry = store[Number(idx)];
    if (!entry) return _m;
    const [kind, tex] = entry;
    return kind === "display" ? `\n\n$$\n${tex.trim()}\n$$\n\n` : `$${tex}$`;
  });

  return s;
}

// Splits on fenced blocks (``` / ~~~) and inline code spans so we never touch
// `$` or backslash sequences inside code. Capturing group keeps the delimiters
// in the resulting array at odd indices.
const CODE_SPLIT = /(```[\s\S]*?```|~~~[\s\S]*?~~~|``[^`]*``|`[^`\n]+`)/g;

/**
 * Normalize math delimiters in a markdown string for rendering with
 * remark-math + rehype-katex. See module docs for the exact rules.
 */
export function normalizeMathDelimiters(text: string): string {
  if (!text || typeof text !== "string") return text;
  // Fast path: nothing math-ish to do.
  if (
    !text.includes("$") &&
    !text.includes(String.raw`\(`) &&
    !text.includes(String.raw`\[`)
  ) {
    return text;
  }

  return text
    .split(CODE_SPLIT)
    .map((segment, i) => (i % 2 === 0 ? transformProse(segment) : segment))
    .join("");
}

export default normalizeMathDelimiters;
